import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Check, Close } from '@mui/icons-material';
import PackageIcon from '@components/PackageIcon';
import {
  buildPlanComparisonRows,
  formatPlanCurrency,
  getPlanDisplayName,
  type VoverPlanPack,
} from '@utils/planPacks';

export interface VoverPlansSelectorProps {
  packs: VoverPlanPack[];
  selectedCapacity: number;
  onSelectCapacity: (capacity: number) => void;
  /** Índice do plano recomendado (borda destacada) */
  recommendedIndex?: number;
  /** Texto do botão CTA em cada coluna do header (opcional) */
  showCta?: boolean;
  onCtaClick?: (pack: VoverPlanPack) => void;
  ctaLabel?: string;
  /** Oculta tabela se não houver funcionalidades cadastradas */
  hideComparisonIfEmpty?: boolean;
}

/**
 * Seletor horizontal de planos (timeline evolutiva) + tabela comparativa de funcionalidades.
 */
const VoverPlansSelector: React.FC<VoverPlansSelectorProps> = ({
  packs,
  selectedCapacity,
  onSelectCapacity,
  recommendedIndex,
  showCta = false,
  onCtaClick,
  ctaLabel = 'Contratar',
  hideComparisonIfEmpty = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const comparisonRows = buildPlanComparisonRows(packs);
  const showComparison = comparisonRows.length > 0 || !hideComparisonIfEmpty;

  if (packs.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Nenhum plano configurado. Entre em contato com o suporte.
      </Typography>
    );
  }

  return (
    <Box>
      {/* Timeline horizontal */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'flex-end',
          gap: isMobile ? 2 : 0,
          position: 'relative',
          mb: 4,
          pt: packs.some((p) => p.highlight) ? 3 : 0,
        }}
      >
        {!isMobile && packs.length > 1 && (
          <Box
            sx={{
              position: 'absolute',
              left: '8%',
              right: '8%',
              bottom: 52,
              height: 3,
              bgcolor: 'divider',
              zIndex: 0,
            }}
          />
        )}
        {packs.map((pack, index) => {
          const isSelected = selectedCapacity === pack.capacity;
          const isRecommended = recommendedIndex === index;
          return (
            <Box
              key={`${pack.capacity}-${index}`}
              sx={{
                flex: 1,
                minWidth: isMobile ? '100%' : 0,
                px: isMobile ? 0 : 1,
                zIndex: 1,
                position: 'relative',
              }}
            >
              {pack.highlight && (
                <Chip
                  label={pack.highlight}
                  size="small"
                  color="primary"
                  sx={{
                    position: 'absolute',
                    top: -28,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    fontWeight: 700,
                    fontSize: '0.65rem',
                    maxWidth: '95%',
                  }}
                />
              )}
              <Paper
                elevation={isSelected ? 4 : 1}
                onClick={() => onSelectCapacity(pack.capacity)}
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  textAlign: 'center',
                  border: 2,
                  borderColor: isSelected
                    ? 'primary.main'
                    : isRecommended
                      ? 'info.main'
                      : 'divider',
                  borderRadius: 2,
                  bgcolor: isSelected ? 'primary.main' : 'background.paper',
                  color: isSelected ? 'primary.contrastText' : 'text.primary',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: 'primary.main',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                  <PackageIcon
                    iconName={pack.icon ?? null}
                    sx={{
                      fontSize: 32,
                      color: isSelected ? 'inherit' : 'primary.main',
                    }}
                  />
                </Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ textTransform: 'uppercase' }}>
                  {getPlanDisplayName(pack)}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mt: 0.5,
                    opacity: isSelected ? 0.9 : 0.7,
                  }}
                >
                  Até {pack.capacity} condomínios
                </Typography>
                <Typography variant="h5" fontWeight={800} sx={{ mt: 1.5, lineHeight: 1.1 }}>
                  {formatPlanCurrency(pack.price)}
                </Typography>
                <Typography variant="caption" sx={{ opacity: isSelected ? 0.9 : 0.7 }}>
                  /mês
                </Typography>
                {showCta && onCtaClick && (
                  <Button
                    variant="contained"
                    size="small"
                    fullWidth
                    sx={{
                      mt: 2,
                      bgcolor: isSelected ? 'common.white' : 'primary.main',
                      color: isSelected ? 'primary.main' : 'primary.contrastText',
                      '&:hover': {
                        bgcolor: isSelected ? 'grey.100' : 'primary.dark',
                      },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectCapacity(pack.capacity);
                      onCtaClick(pack);
                    }}
                  >
                    {ctaLabel}
                  </Button>
                )}
              </Paper>
            </Box>
          );
        })}
      </Box>

      {/* Tabela comparativa */}
      {showComparison && (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 700, minWidth: 180 }}>Funcionalidades</TableCell>
                {packs.map((pack, i) => (
                  <TableCell
                    key={`head-${pack.capacity}-${i}`}
                    align="center"
                    sx={{
                      fontWeight: 700,
                      borderLeft: '1px solid',
                      borderColor: 'divider',
                      ...(selectedCapacity === pack.capacity && {
                        bgcolor: 'primary.50',
                        color: 'primary.main',
                      }),
                    }}
                  >
                    {getPlanDisplayName(pack)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {comparisonRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={packs.length + 1} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      Funcionalidades serão exibidas aqui quando cadastradas no admin.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                comparisonRows.map((row) => (
                  <TableRow key={row.feature} hover>
                    <TableCell>{row.feature}</TableCell>
                    {row.includedByPlan.map((included, colIndex) => (
                      <TableCell
                        key={`${row.feature}-${colIndex}`}
                        align="center"
                        sx={{
                          borderLeft: '1px solid',
                          borderColor: 'divider',
                          ...(selectedCapacity === packs[colIndex]?.capacity && {
                            bgcolor: 'action.hover',
                          }),
                        }}
                      >
                        {included ? (
                          <Check color="success" fontSize="small" />
                        ) : (
                          <Close color="disabled" fontSize="small" sx={{ opacity: 0.4 }} />
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default VoverPlansSelector;
